import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    LightbulbOutlined,
    AttachMoney,
    Equalizer,
    Group,
    CheckCircleOutline,
    TipsAndUpdates,
    Calculate,
    EmojiPeople,
    ArrowForwardIos
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {[
                                { id: 'introduction', label: 'ルームシェアと光熱費のリアル' },
                                { id: 'average', label: 'ルームシェアの光熱費平均額' },
                                { id: 'saving-tips', label: '今すぐ使える節約テクニック' },
                                { id: 'cost-sharing', label: '費用分担のコツと注意点' },
                                { id: 'experiences', label: '体験談・みんなの工夫' },
                                { id: 'summary', label: 'まとめ' }
                            ].map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888',
                                        fontWeight: 'bold', borderRadius: 1, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアと光熱費のリアル
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、多様なライフスタイルや住まい方の一つとして注目されている「ルームシェア」。都心の家賃高騰や、仲間との協力的な暮らしを求めて新生活を始める方も増えています。しかし、実際に暮らし始めてみると「光熱費ってどれくらい？」「どうやって負担すればトラブルにならない？」そんな疑問や不安の声もよく耳にします。
                </Typography>
                <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/roommates-dining_roomshare-utility-average-saving.jpg"
                        alt="明るい共有リビングでルームメイトたちが食事を囲みながら光熱費の話題で盛り上がっている様子。全体が暖色系の照明で温かい雰囲気。"
                        style={{ width: '100%', maxWidth: 540, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    />
                </Box>
                <Typography variant="body1">
                    本記事では、「ルームシェアの光熱費」に焦点を当て、<b>平均額</b>、<b>節約術</b>、<b>分担のコツ</b>、<b>リアルな体験談</b>まで、役立つ情報をわかりやすくまとめました。これからルームシェアを始めたい方も、すでに実践中の方も、ぜひ参考にしてください！
                </Typography>
            </Box>

            {/* ルームシェアの光熱費平均額 */}
            <Box id="average" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアの光熱費平均額
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={7}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            ルームシェア（個人間契約・マンション一室シェア）で特に気になるのは毎月の光熱費負担です。<b>光熱費</b>とは主に「電気代」「ガス代」「水道代」を指し、シェア人数や使用状況で変動します。全国平均や実例をもとに、おおよその目安を見てみましょう。
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><LightbulbOutlined color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>電気代：5,000～9,000円/月（3人シェア時）</b>}
                                    secondary="個人の家電使用量や季節（冷暖房の利用）で変動します。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><AttachMoney color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>ガス代：3,000～6,000円/月（3人シェア時）</b>}
                                    secondary="都市ガスの場合。プロパンは割高になりやすいので要確認。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><Equalizer color="primary" /></ListItemIcon>
                                <ListItemText
                                    primary={<b>水道代：2,000～5,000円/月（3人シェア時）</b>}
                                    secondary="料金は定額制の地域もあり。人数が多いほど1人あたりは安くなる傾向。"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            <b>合計：10,000～20,000円/月（3人シェア）</b><br />
                            ※エリア・季節・物件広さ・契約プランにより変動します。<br />
                            一人暮らしと比べて<b>1人あたりの光熱費負担は約20～30％減</b>る場合が多いです。
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <img
                                src="/images/blog/utility-balance_roomshare-utility-average-saving.jpg"
                                alt="電気・ガス・水道の請求書がテーブルに並び、ルームメイトが一緒に費用計算をしている場面。青系と白基調の爽やかな色合い。"
                                style={{ width: '100%', maxWidth: 260, borderRadius: 8 }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Chip label="ポイント" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Typography variant="body2" component="span">
                        人数が増えるほど「一人あたり」の負担は安くなりやすい。<br />
                        ただし、エアコンや給湯の使い方次第で上下します。
                    </Typography>
                </Box>
            </Box>

            {/* 今すぐ使える節約テクニック */}
            <Box id="saving-tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    今すぐ使える節約テクニック
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアならではの<b>協力プレー</b>で、光熱費はもっと抑えられます。具体的な節約術を見ていきましょう！
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><TipsAndUpdates color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>家電の共有・まとめ使い</b>}
                            secondary="冷蔵庫や洗濯機、エアコンなどは共同利用で稼働回数を減らし、無駄な電力消費をカット。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><Calculate color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「同時使用」を意識する</b>}
                            secondary="お風呂や洗濯をできるだけ同じタイミングに集約。給湯や洗濯のエネルギー効率UP！"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><LightbulbOutlined color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>LED化＆待機電力カット</b>}
                            secondary="照明はLEDに交換、使わない家電の主電源オフで電気代を節約。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><Group color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「ルール」や「チェックリスト」を作成</b>}
                            secondary="「エアコンは誰もいない時は切る」「使い終わったら消灯」など、みんなで決めて実践。"
                        />
                    </ListItem>
                </List>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                <CheckCircleOutline color="success" sx={{ mr: 1 }} />
                                チェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" color="disabled" /></ListItemIcon>
                                    <ListItemText primary="キッチン家電もできるだけ共有を" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" color="disabled" /></ListItemIcon>
                                    <ListItemText primary="浴室乾燥・食洗機はタイマーで効率的に" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIos fontSize="small" color="disabled" /></ListItemIcon>
                                    <ListItemText primary="生活リズムを話し合っておく" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <img
                                src="/images/blog/energy-saving_roomshare-utility-average-saving.jpg"
                                alt="節電・節水のためにみんなでルール表を壁に貼り共同生活を工夫している場面。明るい室内とカラフルな表が印象的。"
                                style={{ width: '100%', maxWidth: 220, borderRadius: 8 }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 費用分担のコツと注意点 */}
            <Box id="cost-sharing" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    費用分担のコツと注意点
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「誰がどれだけ負担するか」はトラブルを避けるためにも<b>最初にしっかり決める</b>ことが大切です。人数、部屋の広さ、使い方による差をどう考えるべきか、代表的な分担方法を紹介します。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><Calculate color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「均等割り」</b>}
                            secondary="もっともシンプル。人数で割る方法。公平感がありトラブルも少なめ。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><Equalizer color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「部屋の広さ・専有面積で按分」</b>}
                            secondary="個室の広さや窓の有無などで割り振りを調整。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                        <ListItemText
                            primary={<b>「使用頻度・生活スタイルに応じて」</b>}
                            secondary="在宅勤務や長期不在者がいる場合は柔軟に割合を調整するケースも。"
                        />
                    </ListItem>
                </List>
                <Paper elevation={0} sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <Group color="secondary" sx={{ mr: 1 }} />
                        トラブル防止のアドバイス
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText
                                primary="月ごとの請求・支払いを明確に。LINEやアプリで履歴を残す"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText
                                primary="領収書や明細は共有スペースにまとめておく"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText
                                primary="光熱費の変動が大きい月は見直しや話し合いを"
                            />
                        </ListItem>
                    </List>
                </Paper>
            </Box>

            {/* 体験談・みんなの工夫 */}
            <Box id="experiences" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    体験談・みんなの工夫
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    ケース1：3人暮らし（社会人）・東京都
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「うちは電気・ガス・水道すべて均等割りです。毎月の請求書をLINEグループに画像で共有し、PayPayで即精算。思ったより簡単でトラブルなし！」
                                </Typography>
                                <Chip label="#均等割り #PayPay" size="small" color="primary" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ maxWidth: 345, mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    ケース2：4人暮らし（学生）・大阪府
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「みんなの生活リズムが違うので、在宅時間が長い人は2割増しで負担。光熱費表を冷蔵庫に貼り、毎月チェックしています。」
                                </Typography>
                                <Chip label="#生活スタイル #透明性" size="small" color="secondary" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <Card sx={{ maxWidth: 700, mb: 2 }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    ケース3：2人暮らし（カップル）・札幌市
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「冬場の暖房代で揉めたことがあり、今はエアコンの利用状況を週ごとに記録。ガス代は部屋の広さで案分。話し合いが大事だと痛感しました。」
                                </Typography>
                                <Chip label="#寒冷地 #ガス暖房" size="small" color="info" variant="outlined" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/blog/roommate-discussion_roomshare-utility-average-saving.jpg"
                        alt="リビングテーブルでルームメイトたちが電卓とノートを使いながら光熱費の分担について話し合っている様子。ナチュラルな木目と落ち着いた雰囲気。"
                        style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                </Box>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでの光熱費は、<b>平均額の目安</b>を知り、<b>みんなで協力して節約</b>し、<b>分担ルールを明確</b>にすることで、トラブルなく快適な共同生活が実現します。節約術や分担の工夫は、ちょっとした話し合いとアイデアでぐっと変わります。
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" />
                    <Chip label="光熱費" color="secondary" variant="outlined" />
                    <Chip label="節約術" color="success" variant="outlined" />
                    <Chip label="分担方法" color="info" variant="outlined" />
                </Box>
                <Typography variant="body1">
                    これからルームシェア生活を始める方も、今の住まいをさらに快適にしたい方も、ぜひ本記事を参考に、光熱費の「見える化」と「協力」を実践してみてくださいね！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-utility-average-saving',
    slug: 'roomshare-utility-average-saving',
    title: 'ルームシェアの「光熱費」平均と節約術',
    excerpt: '光熱費の平均額、節約テクニック、費用分担のコツ、体験談などルームシェアの光熱費について詳しく解説します。',
    coverImage: '/images/blog/cover-roomshare-utility_roomshare-utility-average-saving.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。白とウッド調のインテリア、柔らかな日差し、リラックスした雰囲気が印象的で、記事全体の「協力的な共同生活」と「光熱費のリアル」を象徴している。',
    publishedAt: '2024-06-13T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '生活コスト', '節約術'],
    tags: ['光熱費', '節約', '共同生活', '費用分担'],
    author: {
        name: '佐藤 直樹',
        avatar: '/images/avatars/sato-naoki.jpg',
        bio: 'シェアハウス・ルームシェアに精通し、実際の運営やアドバイザー経験も豊富。多様な住まい方と快適な共同生活について情報発信中。',
        profilePicture: '/images/avatars/sato-naoki.jpg'
    },
    viewCount: 1380,
    contentComponent: PostContent
};

export default post;